/**
 * RecipeFilters Component
 *
 * Filter bar for the recipe list with search, time filter,
 * freezable/fresh toggles, sort dropdown, and tag chips.
 */

import { router, Link } from '@inertiajs/react';
import { useState, useEffect, useCallback } from 'react';
import type { FoodTag, RecipeFilters as FilterState } from '@/types/food';
import { getTagStyle } from '@/types/food';

interface RecipeFiltersProps {
  tags: FoodTag[];
  filters: FilterState;
}

/**
 * Group tags by namespace
 */
function groupTagsByNamespace(tags: FoodTag[]): Map<string, FoodTag[]> {
  const grouped = new Map<string, FoodTag[]>();

  tags.forEach((tag) => {
    const namespace = tag.namespace || 'other';
    const existing = grouped.get(namespace) || [];
    grouped.set(namespace, [...existing, tag]);
  });

  // Sort namespaces: diet, origin, meal, then other
  const order = ['diet', 'origin', 'meal', 'course', 'season', 'other'];
  const sorted = new Map<string, FoodTag[]>();

  order.forEach((ns) => {
    if (grouped.has(ns)) {
      sorted.set(ns, grouped.get(ns)!);
    }
  });

  // Add any remaining namespaces
  grouped.forEach((value, key) => {
    if (!sorted.has(key)) {
      sorted.set(key, value);
    }
  });

  return sorted;
}

/**
 * Get display name for namespace
 */
function getNamespaceLabel(namespace: string): string {
  const labels: Record<string, string> = {
    diet: 'Diet',
    origin: 'Origin',
    meal: 'Meal',
    course: 'Course',
    season: 'Season',
    other: '',
  };
  return labels[namespace] || namespace;
}

export function RecipeFilters({ tags, filters }: RecipeFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    new Set(filters.tag_ids || (filters.tag_id ? [filters.tag_id] : []))
  );

  const groupedTags = groupTagsByNamespace(tags);

  // Update filters via Inertia router
  const applyFilters = useCallback(
    (newFilters: Partial<FilterState>) => {
      const params: Record<string, string | string[]> = {};

      // Preserve existing filters
      if (filters.search) params.search = filters.search;
      if (filters.max_time) params.max_time = filters.max_time;
      if (filters.freezable) params.freezable = filters.freezable;
      if (filters.eat_immediately) params.eat_immediately = filters.eat_immediately;
      if (filters.sort) params.sort = filters.sort;
      if (filters.tag_ids?.length) params.tag_ids = filters.tag_ids;

      // Apply new filters
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === null) {
          delete params[key];
        } else if (Array.isArray(value)) {
          if (value.length > 0) {
            params[key] = value;
          } else {
            delete params[key];
          }
        } else {
          params[key] = value;
        }
      });

      router.get('/food/recipes', params, {
        preserveState: true,
        preserveScroll: true,
      });
    },
    [filters]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== (filters.search || '')) {
        applyFilters({ search: searchValue || undefined });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, filters.search, applyFilters]);

  const handleMaxTimeChange = (value: string) => {
    applyFilters({ max_time: value || undefined });
  };

  const handleFreezableChange = (checked: boolean) => {
    applyFilters({ freezable: checked ? 'true' : undefined });
  };

  const handleEatFreshChange = (checked: boolean) => {
    applyFilters({ eat_immediately: checked ? 'true' : undefined });
  };

  const handleSortChange = (value: string) => {
    applyFilters({ sort: value || undefined });
  };

  const handleTagToggle = (tagId: string) => {
    const newSelected = new Set(selectedTagIds);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTagIds(newSelected);
    applyFilters({ tag_ids: Array.from(newSelected) });
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm mb-6">
      {/* Main filter row */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search input */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        {/* Max time dropdown */}
        <select
          value={filters.max_time || ''}
          onChange={(e) => handleMaxTimeChange(e.target.value)}
          className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
        >
          <option value="">Max cooking time</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="45">45 minutes</option>
          <option value="60">1 hour</option>
          <option value="">Any time</option>
        </select>

        {/* Freezable checkbox */}
        <label className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 rounded-lg cursor-pointer text-sm hover:bg-gray-100">
          <input
            type="checkbox"
            checked={filters.freezable === 'true'}
            onChange={(e) => handleFreezableChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Freezable
        </label>

        {/* Eat Fresh checkbox */}
        <label className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 rounded-lg cursor-pointer text-sm hover:bg-gray-100">
          <input
            type="checkbox"
            checked={filters.eat_immediately === 'true'}
            onChange={(e) => handleEatFreshChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Eat Fresh
        </label>

        {/* Sort dropdown */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-gray-500">Sort by:</span>
          <select
            value={filters.sort || ''}
            onChange={(e) => handleSortChange(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
          >
            <option value="">Recently updated</option>
            <option value="highest_rated">Highest rated</option>
            <option value="quickest">Quickest</option>
            <option value="recently_made">Recently made</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {/* Ingredients link */}
        <Link
          href="/food/ingredients"
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
          </svg>
          Ingredients
        </Link>
      </div>

      {/* Tag filters */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mt-4 pt-4 border-t border-gray-200">
          {Array.from(groupedTags.entries()).map(([namespace, namespaceTags]) => (
            <div key={namespace} className="flex items-center gap-1.5">
              {getNamespaceLabel(namespace) && (
                <span className="text-xs font-medium text-gray-500 mr-1">
                  {getNamespaceLabel(namespace)}:
                </span>
              )}
              {namespaceTags.map((tag) => {
                const isSelected = selectedTagIds.has(String(tag.id));
                const style = getTagStyle(tag.namespace);

                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(String(tag.id))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      isSelected
                        ? `${style.activeBg} text-white`
                        : `${style.bg} ${style.text} hover:opacity-80`
                    }`}
                  >
                    {tag.value}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
